import { ChangeDetectorRef, Component } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { GenericDialogComponent } from "../../shared/generic-dialog/generic-dialog.component";
import { CartItemService } from "src/app/services/cart-item/cart-item.service";
import { Router } from "@angular/router";
import { CartDetails, ProductCategories, ProductAttributes } from "@models/product.model";
import { LocalStorageService } from "../../services/local-storage/local-storage.service";
import { debounceTime, finalize, forkJoin, Subject, switchMap, switchMapTo, timer } from "rxjs";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { appConfig } from "../../../environments/app.config";
import { ProductService } from "../../services/product/product.service";
import { TranslateService } from "@ngx-translate/core";
import { BaseCart, CartAttributes, DeleteCartItem, RemoveCartItem, RemoveCartItemsPayload } from "@models/cart.model";
import { WishlistService } from "../../services/wishlistService/wishlist.service";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Location } from "@angular/common";
import { AuthService } from "../../services/auth/auth.service";
import { DialogService } from "../../services/dialog/dialog.service";
import { ColorsPicker } from "../../../utils/color-constants";
import { environment } from "~/src/environments/environment";
import { CouponService } from "../../services/coupon/coupon.service";
import { CouponAttributes } from "@models/coupon.model";
import { UserService } from "../../services/user/user.service";
import {
    NullableAddress,
    NullableRegisteredUserDetails,
    NullableUser,
    UserDetailsDto,
    UserDetailStatus,
} from "@models/user.model";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { CheckoutService } from "../../services/checkout-page/checkout.service";

@Component({
    selector: "app-cart-page",
    templateUrl: "./cart-page.component.html",
    styleUrls: ["./cart-page.component.scss"],
    standalone: false,
})
export class CartPageComponent {
    public isLoggedInUser = false;
    cartItems: CartAttributes[] = [];
    products: CartAttributes[] = [];
    cartId!: number;
    totalMRP!: number;
    totalDiscount!: number;
    totalAmount!: number;
    public isCheckoutEnabled: boolean = false;
    public minOrderValue = appConfig.minOrderValue;
    public loading: boolean = false;
    public categories = ProductCategories;
    public wishlistSkipValue: number = 0;
    public wishlistData: ProductAttributes[] = [];
    public currentStep: number = 1;
    public deliveryCharge: number = 0;
    private showMoveToWishlistButton: boolean = false; // when user is logged in only then show move to wishlist button
    private cartUpdateSubject = new Subject<{
        productId: number;
        structureId: number;
        quantity: number;
        cartUUID: string;
    }>();
    public freeDeliveryAmount = 200;
    public isHavingAddress: boolean = true;
    public availableCoupons: CouponAttributes[] = [];
    public userDetails: NullableRegisteredUserDetails = null;
    public isCouponApplied: boolean = false;
    public currentUserAddress?: NullableAddress;
    public currentUser?: NullableUser;
    public isUserExist: boolean = false;
    public selectedItems = new Map<string, CartAttributes>();

    constructor(
        private dialog: MatDialog,
        private cartItemService: CartItemService,
        private router: Router,
        private localStorageService: LocalStorageService,
        private cartService: CartItemService,
        private cdr: ChangeDetectorRef,
        private productService: ProductService,
        private translate: TranslateService,
        private wishlistService: WishlistService,
        private snackBar: MatSnackBar,
        private location: Location,
        public authService: AuthService,
        private dialogService: DialogService,
        private couponsService: CouponService,
        private userService: UserService,
        private checkoutService: CheckoutService,
    ) {}

    ngOnInit(): void {
        if (this.authService.isUserLoggedIn) {
            this.userService.isUserDetailsExist().subscribe((response) => {
                this.isUserExist = response;
            });
            this.showMoveToWishlistButton = true;
            this.getRegisteredUserDetails();
        } else {
            this.isUserExist = false;
            this.getGuestUserDetails();
        }
        this.loadCartItems();
        this.cartUpdateSubject
            .pipe(
                debounceTime(250), // Adjust the time as needed
            )
            .subscribe(({ productId, structureId, quantity, cartUUID }) => {
                this.updateCartQuantity(productId, structureId, quantity, cartUUID);
            });
        this.getsuggestedProducts();
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private getRegisteredUserDetails(): void {
        this.userService.getUserDetails().subscribe((response) => {
            if (response) {
                this.userDetails = response;
                this.currentUser = response.user;
                this.currentUserAddress = response.addresses?.find((one) => one.isPreferred);
            }
        });
    }

    private getGuestUserDetails(): void {
        this.currentUserAddress = this.checkoutService.getAddressFromLocalStorage();
        this.currentUser = this.checkoutService.getCurrentUserFromLocalStorage();
    }

    private loadCartItems(): void {
        this.loading = true;
        timer(100)
            .pipe(
                switchMap(() => this.cartItemService.getCartItems()), // Fetch cart items after delay
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe({
                next: (items) => {
                    this.cartItems = items;
                    const updatedItemsInCart = this.cartItems.map(this.cartItemService.mapToCartItem); // To get the latest item from local ae well as db with cross browser
                    if (updatedItemsInCart.length) {
                        this.localStorageService.setCartItems(updatedItemsInCart);
                    } else {
                        this.localStorageService.removeAllCartItems();
                    }
                    this.products = this.cartItems.map((item) => ({
                        product: item.product!,
                        structure: item.structure!,
                        singleBlockInfo: item.singleBlockInfo!,
                        cartUUID: item.cartUUID,
                        cartId: item.id,
                    }));

                    this.calculateTotalValues();
                    const inActiveIds = this.products
                        .filter((item) => item.product?.isActive === false)
                        .map((item) => ({
                            id: item.product?.id,
                            cartUUID: item.cartUUID,
                        }));

                    if (inActiveIds.length) {
                        this.openDialogUnavailable(inActiveIds);
                    }
                },
                error: (error) => {
                    throw error;
                },
            });
    }

    private openDialogUnavailable(inActiveIds: { id: number; cartUUID?: string }[]): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.UnavailableProducts,
                title: this.getTranslation("genericDialogBox.unavailableProductCart.title"),
                content: this.getTranslation("genericDialogBox.unavailableProductCart.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.removeBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                const removeObservables = inActiveIds.map(({ id, cartUUID }) => {
                    const items: RemoveCartItem = { productId: id };
                    if (cartUUID) {
                        items.cartUUID = cartUUID;
                    }
                    return this.cartItemService.removeCartItem([items]);
                });

                forkJoin(removeObservables).subscribe(() => {
                    this.loadCartItems();
                });
            }
        });
    }

    private calculateTotalValues(): void {
        this.cartItemService.calculateTotalMRP(
            this.cartItems.filter((item) => (item.structure ? item.structure : item.product?.isActive)),
        );
        this.cdr.detectChanges();
        this.totalMRP = this.cartItemService.totalMRP;
        this.totalDiscount = this.cartItemService.totalDiscount;
        this.totalAmount = this.cartItemService.subTotal;
        this.isCheckoutEnabled = this.totalAmount >= appConfig.minOrderValue;
    }

    public onDeleteCartItem(item: CartAttributes): void {
        this.openDialog(item);
    }

    private openDialog(item: CartAttributes): void {
        const hasSecondButton = !item.singleBlockInfo && !item.structure && this.showMoveToWishlistButton;
        const dialogData = {
            componentName: DialogInvokingComponents.RemoveFromCart,
            title: this.getTranslation("genericDialogBox.removeCart.title"),
            image: item.product?.images?.imagesUrl[0],
            content: this.getTranslation("genericDialogBox.removeCart.content"),
            firstBtn: this.getTranslation("genericDialogBox.button.removeBtn"),
            // only add secondBtn if allowed
            ...(hasSecondButton && {
                secondBtn: this.getTranslation("genericDialogBox.button.moveToWishlist"),
            }),
        };
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: dialogData,
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === "move") {
                this.moveToWishlist(item.product);
            } else {
                if (result) this.removeProductFromCart(item.product?.id!, item.structure?.id!, item.cartUUID!);
            }
        });
    }

    public onCheckout(): void {
        this.router.navigate(["address"]);
    }

    public onNavigateToProductPage(): void {
        this.router.navigate(["/products"]);
    }

    public loginDialog(): void {
        this.router.navigate(["/login"]);
    }

    public backToHome(): void {
        this.router.navigate(["home"]);
    }

    public getProductQtyInCart(productId: number, structureId: number, cartUUID: string): number {
        return this.localStorageService.getCartedQuantity(productId, cartUUID, structureId);
    }

    public onClick(value: number, productId: number, structureId: number, cartUUID: string): void {
        const quantityInCart = this.getProductQtyInCart(productId, structureId, cartUUID);
        const quantity = quantityInCart + value;
        this.cartUpdateSubject.next({ productId, structureId, quantity, cartUUID });
    }

    private updateCartQuantity(productId: number, structureId: number, quantity: number, cartUUID: string): void {
        if (quantity < 1) {
            this.removeProductFromCart(productId, structureId, cartUUID);
        } else if (quantity <= 99) {
            this.updateProductQuantityInCart(productId, structureId, quantity, cartUUID);
        }
    }

    private removeProductFromCart(id: number, structureId?: number, cartUUID?: string): void {
        const items: RemoveCartItem = {};
        if (cartUUID) {
            items.cartUUID = cartUUID;
        }

        if (id) {
            items.productId = id;
        }

        if (structureId) {
            items.structureId = structureId;
        }

        this.cartService.removeCartItem([items]).subscribe((response) => {
            if (response) {
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);

                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
                const next = new Map(this.selectedItems);
                [items].forEach((item) => {
                    next.delete(this.buildKey(item.productId!, item.cartUUID));
                });
                this.selectedItems = next;
                this.loadCartItems();
            }
        });
    }

    private updateProductQuantityInCart(
        productId: number,
        structureId: number,
        quantity: number,
        cartUUID: string,
    ): void {
        this.cartService.updateCartQuantity(productId, quantity, cartUUID, structureId).subscribe(() => {
            const item = this.cartItems.find((item) => item.product?.id === productId && item.cartUUID === cartUUID);
            if (item) {
                item.quantity = quantity;
                this.calculateTotalValues();
            }
        });
    }

    public onInputChange(event: Event, productId: number, structureId: number, cartUUID: string): void {
        let inputValue = (event.target as HTMLInputElement).value.trim();
        let quantity = parseInt(inputValue, 10);

        if (isNaN(quantity)) {
            return;
        } else if (quantity < 1) {
            (event.target as HTMLInputElement).value = "1";
            quantity = parseInt((event.target as HTMLInputElement).value, 10);
        } else if (inputValue.length > 2) {
            inputValue = inputValue.slice(0, 2);
            (event.target as HTMLInputElement).value = inputValue;
            return;
        }

        this.cartUpdateSubject.next({ productId, structureId, quantity, cartUUID });
    }

    public onBlur(event: Event, productId: number, structureId: number, cartUUID: string): void {
        const inputValue = (event.target as HTMLInputElement).value.trim();
        if (!inputValue) {
            (event.target as HTMLInputElement).value = "1";
            const quantity = parseInt((event.target as HTMLInputElement).value, 10);
            this.cartUpdateSubject.next({ productId, structureId, quantity, cartUUID });
        }
    }

    public onClickProduct(productId: number): void {
        this.router.navigate([`products/${productId}`]);
    }

    public getFacesByType(data: CartAttributes, type: string): string[] {
        return this.cartItemService.getFacesByType(data.singleBlockInfo!, type);
    }

    public getFaceColors(colorObject: object | undefined): { key: string; value: string }[] {
        if (!colorObject) {
            return [];
        }
        return Object.entries(colorObject).map(([key, value]) => ({
            key,
            value: value as string, // Ensure the value is treated as a string
        }));
    }

    public getdiscountPercentage(product: ProductAttributes): number {
        return Math.round(((product.price - product?.discountedPrice!) / product.price) * 100);
    }

    public moveToWishlist(product: ProductAttributes): void {
        const productId = product.id;

        const finalizeMove = () => {
            this.removeProductFromCart(productId);
            this.loadCartItems();
        };

        if (this.wishlistService.getAllwishlistIDs.productIDs.includes(productId)) {
            finalizeMove();
        } else {
            this.wishlistService.addToWishList([productId]).subscribe(finalizeMove);
        }
    }

    public goBack(): void {
        this.location.back();
    }

    public clickImage(productId: number): void {
        this.router.navigate([`products/${productId}`]);
    }

    public isCategoryBuyBlocks(category: string[] | undefined): boolean {
        return Array.isArray(category) && category.includes(ProductCategories.BuyBlocks);
    }

    public goTohomePage(): void {
        this.router.navigate([`home`]);
    }

    public getColorValue(color: string): string | undefined {
        return ColorsPicker.find((c) => c.name === color)?.value;
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    public suggestedProducts: ProductAttributes[] = [];

    private getsuggestedProducts() {
        this.productService
            .getAllActiveProducts(environment.dynamicPagination.pageIndex, 4, "", ProductCategories.BestSeller)
            .subscribe((response) => {
                this.suggestedProducts = response.rows;
            });
    }

    public fetchAvailableCoupons(mobileNumber: string): void {
        this.couponsService.getUserCoupons().subscribe({
            next: (response) => {
                this.availableCoupons = response.filter((data) => {
                    return (
                        !data.isUsed?.userIds.includes(mobileNumber) &&
                        Object.values(data.discountType).find((type) => type === "Users")
                    );
                });
            },
        });
    }

    public toogleFavorite(product: ProductAttributes): void {
        if (this.authService.isUserLoggedIn) {
            if (this.isInWishlist(product)) {
                this.wishlistService.deleteFromMyWishlist(product.id).subscribe();
            } else {
                this.wishlistService.addToWishList([product.id]).subscribe();
            }
        } else {
            this.dialogService.loginDialog();
        }
    }

    public isInWishlist(product: ProductAttributes): boolean {
        if (this.authService.isUserLoggedIn) {
            return this.wishlistService.getAllwishlistIDs?.productIDs.includes(product.id);
        } else {
            return false;
        }
    }

    public buildKey(productId: number, cartUUID?: string): string {
        return `${productId}|${cartUUID ?? ""}`;
    }

    public isItemSelected(item: CartDetails): boolean {
        return this.selectedItems.has(this.buildKey(item.product.id, item.cartUUID));
    }

    public toggleSelectCart(item: CartDetails): void {
        const key = this.buildKey(item.product.id, item.cartUUID);

        if (this.selectedItems.has(key)) {
            this.selectedItems.delete(key);
        } else {
            this.selectedItems.set(key, {
                productID: item.product.id,
                cartUUID: item.cartUUID,
            });
        }
    }

    public isAllSelected(): boolean {
        if (this.cartItems.length === 0) {
            return false;
        }

        return this.cartItems.every((item) => this.selectedItems.has(this.getCartItemKey(item)));
    }

    public isSomeSelected(): boolean {
        const selectedCount = this.cartItems.filter((item) => this.selectedItems.has(this.getCartItemKey(item))).length;

        return selectedCount > 0 && selectedCount < this.cartItems.length;
    }

    public toggleSelectAll(event: MatCheckboxChange): void {
        if (event.checked) {
            this.cartItems.forEach((item) => {
                const key = this.getCartItemKey(item);
                this.selectedItems.set(key, item);
            });
        } else {
            this.selectedItems.clear();
        }
    }

    public onClickMultipleCarts(action: string): void {
        if (this.selectedItems.size > 0) {
            this.openDialogForMultipleCarts();
        } else {
            this.openSnackBar(
                action === "remove" ? "Select any item to remove from bag" : "Select any item to move to wishlist",
                SnackBarConfig.Actions.OKAY,
            );
        }
    }

    public isSelected(item: CartAttributes): boolean {
        const key = this.getCartItemKey(item);
        return this.selectedItems.has(key);
    }

    public toggleItem(item: CartAttributes, checked: boolean): void {
        const key = this.getCartItemKey(item);

        if (checked) {
            this.selectedItems.set(key, item);
        } else {
            this.selectedItems.delete(key);
        }
    }

    public getCartItemKey(item: CartAttributes): string {
        if (item.cartUUID) {
            return `uuid:${item.cartUUID}`;
        }

        if (item.product) {
            return `product:${item.product.id}`;
        }

        if (item.structure) {
            return `structure:${item.structure.id}`;
        }

        return "";
    }

    public openDialogForMultipleCarts(): void {
        const dialogData = {
            componentName: DialogInvokingComponents.RemoveFromCart,
            title: `Move ${this.selectedItems.size} Item(s)`,
            content: `Are you sure you want to move these ${this.selectedItems.size} items from your cart?`,
            firstBtn: "Remove",
            secondBtn: "Move to Wishlist",
        };
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: dialogData,
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === "move") {
                this.moveMultipleCartstoWishlist();
            } else {
                if (result) this.removeMultipleCarts();
            }
        });
    }

    public removeMultipleCarts(): void {
        const items: DeleteCartItem[] = Array.from(this.selectedItems.values()).flatMap((item): DeleteCartItem[] => {
            if (item.product) {
                return [
                    {
                        type: "productId",
                        id: item.product.id,
                        uuid: item.cartUUID ?? null,
                    },
                ];
            }

            if (item.structure) {
                return [
                    {
                        type: "structureId",
                        id: item.structure.id,
                        uuid: item.cartUUID ?? null,
                    },
                ];
            }

            return [];
        });

        const payload: RemoveCartItemsPayload = {
            items,
        };

        this.cartItemService.removeMultipleCartItem(payload).subscribe((response) => {
            if (response) {
                this.openSnackBar(response, SnackBarConfig.Actions.OKAY);
                this.selectedItems.clear();
                this.loadCartItems();
            }
        });
    }

    public moveMultipleCartstoWishlist(): void {
        const items = Array.from(this.selectedItems.values());
        const productIds = items.map((item) => item.product?.id).filter((id): id is number => id !== undefined);
        const existing = this.wishlistService.getAllwishlistIDs.productIDs;
        const toAdd = productIds.filter((id) => !existing.includes(id!));

        const finalizeMove = () => {
            this.removeMultipleCarts();
        };

        if (!toAdd.length) {
            finalizeMove();
        } else {
            this.wishlistService.addToWishList(toAdd).subscribe(finalizeMove);
        }
    }

    public addAddress(): void {
        const dialogData = {
            componentName: DialogInvokingComponents.AddAddress,
            title: "Add Your Details",
            withUserProfileDetails: true,
            hideAgeAndShowMobile: this.authService.isUserLoggedIn ? false : true,
        };
        this.dialogService
            .openDialog(dialogData, "90%", { disableClose: true })
            .afterClosed()
            .subscribe((response) => {
                if (response) {
                    const payload = response.details ? { ...response.details } : response;
                    this.handleUser(payload);
                }
            });
    }

    private handleUser(payload: UserDetailsDto) {
        if (!this.authService.isUserLoggedIn) {
            //guest user
            if (!this.isUserExist) {
                // No profile yet
                this.addUserDetailsInLocalStorage(payload);
            }
        } else {
            this.userService.addUserDetails(payload).subscribe(() => {
                this.openSnackBar(UserDetailStatus.Add, SnackBarConfig.Actions.CLOSE);
                this.getRegisteredUserDetails();
            });
        }
    }

    private addUserDetailsInLocalStorage(payload: UserDetailsDto): void {
        this.checkoutService.setUserInLocalStorage(payload.userDetails);
        this.checkoutService.setAddressInLocalStorage(payload.addressDetails);
        this.loading = true;
        setTimeout(() => {
            this.getGuestUserDetails();
            this.loading = false;
        }, 1000);
    }
}
